import React, { useEffect, useState } from 'react'
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { TeamService } from '../../../../shared/services/teamService'
import ConfirmDialog from '../../../../shared/components/ConfirmDialog'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../../../shared/lib/format'

interface Invitation {
  _id: string
  invitedEmail: string
  role: 'editor' | 'viewer'
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  expiresAt: string
  emailLogs: Array<{
    _id: string
    status: string
    sentAt: string
    error?: string
    deliveryAttempts?: number
    lastAttemptAt?: string
  }>
  lastEmailStatus: string
  totalEmailAttempts: number
}

interface TeamInvitationListProps {
  teamId: string
  onInvitationUpdate: () => void
}

const TeamInvitationList: React.FC<TeamInvitationListProps> = ({
  teamId,
  onInvitationUpdate,
}) => {
  const { t } = useTranslation()
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null)

  useEffect(() => {
    fetchInvitations()
  }, [teamId])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const data = await TeamService.getInvitations(teamId)
      if (data.success) {
        setInvitations((data.invitations ?? []) as unknown as Invitation[])
      }
    } catch (error) {
      console.error('초대 목록 조회 실패:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async (invitationId: string) => {
    if (actionLoading) {
      return
    }

    setActionLoading(invitationId)
    try {
      const data = await TeamService.resendInvitation(teamId, invitationId)

      if (data.success) {
        toast.success(t('team.resendSuccess'))
        fetchInvitations()
        onInvitationUpdate()
      } else {
        toast.error(data.message || t('team.resendError'))
      }
    } catch (error) {
      console.error('초대 재발송 실패:', error)
      toast.error(t('team.resendFailed'))
    } finally {
      setActionLoading(null)
    }
  }

  const requestCancel = (invitationId: string) => {
    setPendingCancelId(invitationId)
  }

  const handleCancel = async (invitationId: string) => {
    if (actionLoading) {
      return
    }

    setActionLoading(invitationId)
    try {
      const data = await TeamService.cancelInvitation(teamId, invitationId)

      if (data.success) {
        toast.success(t('team.cancelSuccess'))
        fetchInvitations()
        onInvitationUpdate()
      } else {
        toast.error(data.message || t('team.cancelError'))
      }
    } catch (error) {
      console.error('초대 취소 실패:', error)
      toast.error(t('team.cancelFailed'))
    } finally {
      setActionLoading(null)
    }
  }

  const getStatusBadge = (invitation: Invitation) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: ClockIcon,
        text: t('team.statusPending'),
      },
      accepted: {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircleIcon,
        text: t('team.statusAccepted'),
      },
      declined: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircleIcon,
        text: t('team.statusDeclined'),
      },
      expired: {
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: XCircleIcon,
        text: t('team.statusExpired'),
      },
    } as const

    const config = statusConfig[invitation.status]
    const Icon = config.icon

    return (
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${config.color}`}
      >
        <Icon className="mr-1 h-3.5 w-3.5" />
        {config.text}
      </span>
    )
  }

  const getEmailStatusBadge = (invitation: Invitation) => {
    if (invitation.lastEmailStatus === 'sent') {
      return (
        <span className="inline-flex items-center text-xs text-green-600">
          <CheckCircleIcon className="mr-1 h-3.5 w-3.5" />
          {t('team.sent')}
        </span>
      )
    }
    if (invitation.lastEmailStatus === 'failed') {
      return (
        <span className="inline-flex items-center text-xs text-red-600">
          <ExclamationTriangleIcon className="mr-1 h-3.5 w-3.5" />
          발송 실패
        </span>
      )
    }
    return <span className="text-xs text-gray-500">미발송</span>
  }

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      editor: { color: 'bg-blue-100 text-blue-800', text: '편집자' },
      viewer: { color: 'bg-gray-100 text-gray-800', text: '뷰어' },
    } as const

    const config =
      roleConfig[role as keyof typeof roleConfig] || roleConfig.viewer

    return (
      <span
        className={`rounded px-2 py-0.5 text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (invitations.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 py-8 text-center">
        <ClockIcon className="mx-auto mb-3 h-10 w-10 text-gray-400" />
        <h3 className="mb-1 text-sm font-medium text-gray-900">
          {t('team.noInvites')}
        </h3>
        <p className="text-xs text-gray-500">{t('team.noInvitesHint')}</p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            {t('team.sentInvites')} ({invitations.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          {invitations.map(invitation => (
            <div
              key={invitation._id}
              className="flex items-center justify-between p-4 transition-colors hover:bg-gray-50"
            >
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex items-center space-x-3">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {invitation.invitedEmail}
                  </p>
                  {getStatusBadge(invitation)}
                  {getRoleBadge(invitation.role)}
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  {getEmailStatusBadge(invitation)}
                  <span>•</span>
                  <span>발송 시도: {invitation.totalEmailAttempts}회</span>
                  <span>•</span>
                  <span>만료: {formatDate(invitation.expiresAt)}</span>
                </div>

                {invitation.lastEmailStatus === 'failed' &&
                  invitation.emailLogs[0]?.error && (
                    <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                      <strong>오류:</strong> {invitation.emailLogs[0].error}
                    </div>
                  )}
              </div>

              {invitation.status === 'pending' && (
                <div className="ml-4 flex items-center space-x-2">
                  <button
                    onClick={() => handleResend(invitation._id)}
                    disabled={actionLoading === invitation._id}
                    className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="재발송"
                  >
                    <ArrowPathIcon
                      className={`h-4 w-4 ${actionLoading === invitation._id ? 'animate-spin' : ''}`}
                    />
                  </button>
                  <button
                    onClick={() => requestCancel(invitation._id)}
                    disabled={actionLoading === invitation._id}
                    className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="취소"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!pendingCancelId}
        title={t('team.cancelInviteTitle')}
        description={t('team.cancelInviteDescription')}
        confirmLabel={t('team.cancelInvite')}
        isBusy={!!actionLoading}
        onCancel={() => setPendingCancelId(null)}
        onConfirm={() => {
          const target = pendingCancelId
          setPendingCancelId(null)
          if (target) {
            handleCancel(target)
          }
        }}
      />
    </>
  )
}

export default TeamInvitationList
